// Das Dashboard: die erste Seite nach dem Anmelden.
//
// Es beantwortet in dieser Reihenfolge drei Fragen:
//   1. Läuft der Sender?        – die Heldenkarte ganz oben
//   2. Was liegt an?            – die Kacheln, Dringendes zuerst
//   3. Wie lief es?             – die Hörerkurve darunter (daneben, solange
//                                 nicht alles eingerichtet ist, die ersten Schritte)
//
// Welche Kacheln jemand sieht, entscheidet der Server anhand der Rolle. Hier
// wird nichts zusätzlich ausgeblendet: Was ankommt, gehört dieser Person.

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { api, type DashboardKachel } from "../api";
import { StreamingStatusPanel } from "../components/StreamingStatusPanel";
import { ListenerStatsChart } from "../components/ListenerStatsChart";
import { Page } from "../components/ui";

type Onboarding = Awaited<ReturnType<typeof api.getOnboarding>>;

/** Farbe und Symbol je nach Dringlichkeit – die gleiche Sprache wie überall. */
const TON: Record<DashboardKachel["ton"], { symbol: string; flaeche: string; zahl: string; text: string }> = {
  warnung: {
    symbol: "M12 4l9 16H3zM12 10v4M12 17h.01",
    flaeche: "bg-red-500/10 text-red-600 dark:text-red-400 ring-red-500/20",
    zahl: "text-red-600 dark:text-red-400",
    text: "Dringend",
  },
  achtung: {
    symbol: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 8v5M12 16h.01",
    flaeche: "bg-amber-500/10 text-amber-600 dark:text-amber-400 ring-amber-500/20",
    zahl: "text-amber-600 dark:text-amber-400",
    text: "Achtung",
  },
  ruhig: {
    symbol: "M4 12h16",
    flaeche: "bg-input text-muted ring-[color:var(--border)]",
    zahl: "text-primary",
    text: "Ruhig",
  },
  gut: {
    symbol: "M5 12l4 4 10-10",
    flaeche: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 ring-emerald-500/20",
    zahl: "text-primary",
    text: "In Ordnung",
  },
};

function gruss(stunde: number): string {
  if (stunde < 5) return "Gute Nacht";
  if (stunde < 11) return "Guten Morgen";
  if (stunde < 18) return "Guten Tag";
  return "Guten Abend";
}

function Kachel({ k }: { k: DashboardKachel }): JSX.Element {
  const ton = TON[k.ton];
  return (
    <Link
      to={k.ziel}
      className="card group relative flex flex-col p-4 transition hover:-translate-y-px hover:border-[color:var(--border-strong)] hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-primary"
      aria-label={`${k.label}: ${k.wert} ${k.einheit ?? ""}${k.hinweis ? `, ${k.hinweis}` : ""}`}
    >
      <div className="flex items-center gap-2.5">
        <span className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg ring-1 ring-inset ${ton.flaeche}`} title={ton.text}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={ton.symbol} /></svg>
        </span>
        <span className="min-w-0 flex-1 text-[0.85rem] font-medium leading-tight text-secondary sm:truncate">{k.label}</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
             className="flex-shrink-0 -translate-x-1 text-muted opacity-0 transition group-hover:translate-x-0 group-hover:opacity-100" aria-hidden="true">
          <path d="M5 12h14M13 6l6 6-6 6" />
        </svg>
      </div>
      <div className="mt-3 flex items-baseline gap-1.5">
        <span className={`text-[2rem] font-semibold leading-none tracking-tight tabular-nums ${ton.zahl}`}>{k.wert}</span>
        {k.einheit && <span className="text-sm text-muted">{k.einheit}</span>}
      </div>
      {k.hinweis && <div className="mt-2 text-xs leading-relaxed text-muted">{k.hinweis}</div>}
    </Link>
  );
}

/** Fortschrittsring: wie weit die Einrichtung ist, auf einen Blick. */
function Ring({ anteil }: { anteil: number }): JSX.Element {
  const r = 20;
  const u = 2 * Math.PI * r;
  return (
    <div className="relative h-14 w-14 flex-shrink-0">
      <svg viewBox="0 0 48 48" className="h-14 w-14 -rotate-90" aria-hidden="true">
        <circle cx="24" cy="24" r={r} fill="none" strokeWidth="5" style={{ stroke: "var(--bg-sunken)" }} />
        <circle
          cx="24" cy="24" r={r} fill="none" strokeWidth="5" strokeLinecap="round"
          style={{ stroke: "rgb(var(--brand-primary-rgb))", strokeDasharray: u, strokeDashoffset: u * (1 - anteil), transition: "stroke-dashoffset .5s ease" }}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold tabular-nums text-primary">
        {Math.round(anteil * 100)}%
      </span>
    </div>
  );
}

/** Was einem neuen Radio noch fehlt; verschwindet, sobald alles erledigt ist. */
function FirstSteps({ data }: { data: Onboarding }): JSX.Element {
  const doneCount = data.steps.filter((s) => s.done).length;
  return (
    <section className="card overflow-hidden">
      <div className="flex items-center gap-4 border-b border-default px-5 py-4">
        {/* Ein Ring sagt mehr als eine Zahl - man sieht, wie weit es noch ist. */}
        <Ring anteil={data.steps.length ? doneCount / data.steps.length : 0} />
        <div className="min-w-0">
          <h2 className="card-title">Erste Schritte</h2>
          <p className="mt-0.5 text-xs text-muted">{doneCount} von {data.steps.length} erledigt</p>
        </div>
      </div>
      <ul className="p-2">
        {data.steps.map((s) => {
          const inhalt = (
            <>
              <span className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full ${s.done ? "bg-emerald-500 text-white" : "border-2 border-[color:var(--border-strong)]"}`}>
                {s.done && (
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l4 4 10-10" /></svg>
                )}
              </span>
              <span className={`min-w-0 flex-1 ${s.done ? "text-muted line-through" : "font-medium text-primary"}`}>{s.label}</span>
              {!s.done && (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 text-muted transition group-hover:translate-x-0.5 group-hover:text-brand-primary" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
              )}
            </>
          );
          const zeile = "group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm";
          return (
            <li key={s.id}>
              {s.done ? (
                <div className={zeile}>{inhalt}</div>
              ) : s.external ? (
                <a href={s.link} target="_blank" rel="noreferrer" className={`${zeile} hover:bg-surface-hover`}>{inhalt}</a>
              ) : (
                <Link to={s.link} className={`${zeile} hover:bg-surface-hover`}>{inhalt}</Link>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function DashboardPage(): JSX.Element {
  const { user } = useAuth();
  const [kacheln, setKacheln] = useState<DashboardKachel[] | null>(null);
  const [fehler, setFehler] = useState(false);
  const [onboarding, setOnboarding] = useState<Onboarding | null>(null);
  const jetzt = new Date();

  const laden = useCallback(async () => {
    try {
      const d = await api.getDashboard();
      setKacheln(d.kacheln);
      setFehler(false);
    } catch {
      setFehler(true);
    }
  }, []);

  useEffect(() => {
    void laden();
    // Eine Minute reicht: Das Dashboard ist eine Übersicht, kein Messgerät.
    // Was wirklich live ist, steht in der Leiste darüber und holt sich alle
    // zehn Sekunden neue Werte.
    const t = setInterval(() => void laden(), 60_000);
    return () => clearInterval(t);
  }, [laden]);

  // Die ersten Schritte werden hier geholt (nicht in der Karte selbst), damit
  // die Hörerkurve die ganze Breite bekommt, sobald alles erledigt ist.
  useEffect(() => {
    api.getOnboarding().then(setOnboarding).catch(() => setOnboarding(null));
  }, []);
  const zeigeSchritte = Boolean(onboarding && !onboarding.done);

  return (
    <Page className="space-y-8">
      <header className="animate-fade-in">
        <div className="section-label">
          {jetzt.toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          {user?.tenant_name ? ` · ${user.tenant_name}` : ""}
        </div>
        <h1 className="mt-2 text-3xl font-semibold tracking-tight text-primary sm:text-[2.25rem] sm:leading-tight">
          {gruss(jetzt.getHours())}, {user?.display_name || user?.email?.split("@")[0]}
        </h1>
      </header>

      <StreamingStatusPanel />

      <section aria-labelledby="anliegen">
        <h2 id="anliegen" className="section-label mb-3">
          Was liegt an
        </h2>
        {fehler && (
          <div className="callout callout-warning">
            Die Übersicht ließ sich nicht laden. Der Sendebetrieb ist davon nicht betroffen.
          </div>
        )}
        {!kacheln && !fehler && (
          // Platzhalter in der Form der Kacheln: kein Springen, wenn sie kommen.
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="card h-[118px] animate-pulse" />
            ))}
          </div>
        )}
        {kacheln && kacheln.length > 0 && (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-4">
            {kacheln.map((k) => <Kachel key={k.id} k={k} />)}
          </div>
        )}
        {kacheln && kacheln.length === 0 && (
          <div className="card p-5 text-sm text-muted">
            Für deine Rolle gibt es hier nichts zu tun. Alles Weitere steht links im Menü.
          </div>
        )}
      </section>

      <div className={`grid items-start gap-6 ${zeigeSchritte ? "xl:grid-cols-[minmax(0,1fr)_360px]" : ""}`}>
        {zeigeSchritte && onboarding && (
          <div className="xl:order-2">
            <FirstSteps data={onboarding} />
          </div>
        )}
        <div className="min-w-0 xl:order-1">
          <ListenerStatsChart />
        </div>
      </div>
    </Page>
  );
}
